import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink } from 'lucide-react';
import { VulnerabilitySearch } from '@/components/vulnerability/VulnerabilitySearch';
import { api } from '@/lib/api/config';

interface CVE {
  cve_id: string;
  summary: string;
  cvss: number;
  cvss_version: number;
  cvss_v2: number | null;
  cvss_v3: number | null;
  epss: number | null;
  ranking_epss: number | null;
  kev: boolean;
  propose_action: string | null;
  ransomware_campaign: string | null;
  references: string[];
  published_time: string;
}

// Updated to match the nested structure from the API
interface CVEResponse {
  cves: {
    cves: CVE[];
  };
}

function getCVSSColor(score: number): string {
  if (score >= 9.0) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  if (score >= 7.0) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  if (score >= 4.0) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VulnerabilityDatabase() {
  const [latestCVEs, setLatestCVEs] = useState<CVE[]>([]);
  const [searchResults, setSearchResults] = useState<CVE[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSearch, setCurrentSearch] = useState('');

  useEffect(() => {
    fetchLatestCVEs();
  }, []);

  const fetchLatestCVEs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<CVEResponse>('/api/vulnerabilities/latest');
      
      // Access the nested cves array
      const cves = response.data.cves.cves;
      if (Array.isArray(cves)) {
        setLatestCVEs(cves);
      } else {
        console.error('Invalid CVE data format:', response.data);
        setLatestCVEs([]);
        setError('Failed to load CVE data. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching CVEs:', err);
      setLatestCVEs([]);
      setError('Failed to load CVE data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query) {
      setSearchResults([]);
      setCurrentSearch('');
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<CVEResponse>(`/api/vulnerabilities/search?product=${encodeURIComponent(query)}`);
      
      // Access the nested cves array
      const cves = response.data.cves.cves;
      if (Array.isArray(cves)) {
        setSearchResults(cves);
        setCurrentSearch(query);
      } else {
        console.error('Invalid search results format:', response.data);
        setSearchResults([]);
        setError(`No results were found for "${query}", try a different product name`);
      }
    } catch (err) {
      console.error('Error searching CVEs:', err);
      setSearchResults([]);
      setError(`No results were found for "${query}", try a different product name`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchResults([]);
    setCurrentSearch('');
    setError(null);
  };

  const CVECard = ({ cve }: { cve: CVE }) => (
    <Card className="overflow-hidden">
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium">
            <a
              href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-foreground"
            >
              {cve.cve_id}
              <ExternalLink className="h-4 w-4" />
            </a>
          </CardTitle>
          {cve.cvss && (
            <Badge className={getCVSSColor(cve.cvss)}>
              CVSS {cve.cvss}
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          Published: {formatDate(cve.published_time)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">{cve.summary}</p>
        
        <div className="space-y-2">
          {cve.references && cve.references.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-sm font-medium">References:</h4>
              <div className="space-y-1">
                {cve.references.map((ref, index) => (
                  <a
                    key={index}
                    href={ref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {ref.split('/')[2]}
                  </a>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2 pt-2">
            {cve.kev && (
              <Badge variant="destructive">
                Known Exploited
              </Badge>
            )}
            {cve.ransomware_campaign && (
              <Badge variant="destructive">
                Ransomware
              </Badge>
            )}
            {cve.epss && (
              <Badge variant="secondary">
                EPSS: {(cve.epss * 100).toFixed(2)}%
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 pb-6">
      <div className="p-4 sm:p-6">
        <VulnerabilitySearch 
          onSearch={handleSearch}
          onClear={handleClearSearch}
          placeholder="Search by product name"
          searchQuery={currentSearch}
        />
      </div>

      {error && (
        <div className="text-center text-muted-foreground px-6">
          {error}
        </div>
      )}

      <div className="px-6 space-y-6">
        {isLoading ? (
          <div className="text-center text-muted-foreground">Loading...</div>
        ) : searchResults.length > 0 ? (
          <>
            <h2 className="text-2xl font-ibm-condensed">Search Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.map((cve) => (
                <CVECard key={cve.cve_id} cve={cve} />
              ))}
            </div>
          </>
        ) : !currentSearch && Array.isArray(latestCVEs) && latestCVEs.length > 0 ? (
          <>
            <h2 className="text-2xl font-ibm-condensed">Latest CVEs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {latestCVEs.map((cve) => (
                <CVECard key={cve.cve_id} cve={cve} />
              ))}
            </div>
          </>
        ) : currentSearch ? (
          <div className="text-center text-muted-foreground">
            No results found for "{currentSearch}"
          </div>
        ) : null}
      </div>
    </div>
  );
}